import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Power, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Badge } from "../components/Badge";
import { ErrorPanel, Loading } from "../components/Loading";
import {
  EntityDrawer,
  PageHeader,
  StatutoryDefaultsCallout,
} from "../components/ui";
import { BilingualText } from "../components/ui/BilingualText";
import { api, ApiError } from "../lib/api";
import type { AllowanceDetail } from "../lib/api";
import { useLegalEntityGate } from "../lib/legalEntity";

const CATEGORIES = [
  "housing",
  "transport",
  "communication",
  "iqama",
  "medical",
  "performance",
  "other",
] as const;

const TYPES = ["allowance", "deduction", "cost"] as const;
const CALCS = ["fixed", "percent", "per_day", "formula"] as const;

function NewAllowanceDialog({
  legalEntityId,
  onClose,
  onCreated,
}: {
  legalEntityId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    code: "",
    name_en: "",
    name_ar: "",
    allowance_type: "allowance",
    category: "other",
    amount_calculation: "fixed",
    fixed_amount: "0",
    percent: "",
    formula_ref: "",
    included_in_gosi: false,
    included_in_eos_base: false,
    included_in_tax: false,
  });
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {
        legal_entity_id: legalEntityId,
        code: form.code,
        name_en: form.name_en,
        name_ar: form.name_ar,
        allowance_type: form.allowance_type,
        category: form.category,
        amount_calculation: form.amount_calculation,
        included_in_gosi: form.included_in_gosi,
        included_in_eos_base: form.included_in_eos_base,
        included_in_tax: form.included_in_tax,
      };
      if (form.amount_calculation === "fixed") body.fixed_amount = form.fixed_amount;
      if (form.amount_calculation === "percent") body.percent = form.percent;
      if (form.amount_calculation === "formula") body.formula_ref = form.formula_ref;
      return api.createAllowance(body);
    },
    onSuccess: () => {
      onCreated();
      onClose();
    },
    onError: (e: unknown) => {
      setErrorMsg(e instanceof ApiError ? e.detail : String(e));
    },
  });

  return (
    <EntityDrawer
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={t("policies.allowances_new")}
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-border-subtle px-4 py-2 text-sm font-medium text-text-secondary hover:bg-surface-sunken"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            form="new-allowance-form"
            disabled={mutation.isPending}
            className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-fg hover:bg-brand-hover disabled:opacity-50"
          >
            {mutation.isPending
              ? t("policies.creating")
              : t("policies.create")}
          </button>
        </div>
      }
    >
        <form
          id="new-allowance-form"
          onSubmit={(e) => {
            e.preventDefault();
            mutation.mutate();
          }}
          className="space-y-3 text-sm"
        >
          <label className="block">
            <span className="font-medium text-slate-700">{t("policies.code_required")}</span>
            <input
              required
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              placeholder="HARDSHIP"
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2 font-mono uppercase"
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="font-medium text-slate-700">{t("policies.name_en_required")}</span>
              <input
                required
                value={form.name_en}
                onChange={(e) => setForm({ ...form, name_en: e.target.value })}
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
              />
            </label>
            <label className="block">
              <span className="font-medium text-slate-700">{t("policies.name_ar_required")}</span>
              <input
                required
                dir="rtl"
                value={form.name_ar}
                onChange={(e) => setForm({ ...form, name_ar: e.target.value })}
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
              />
            </label>
            <label className="block">
              <span className="font-medium text-slate-700">{t("common.type")}</span>
              <select
                value={form.allowance_type}
                onChange={(e) =>
                  setForm({ ...form, allowance_type: e.target.value })
                }
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
              >
                {TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="font-medium text-slate-700">{t("policies.allowances_category_label")}</span>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="font-medium text-slate-700">{t("policies.allowances_calc_label")}</span>
              <select
                value={form.amount_calculation}
                onChange={(e) =>
                  setForm({ ...form, amount_calculation: e.target.value })
                }
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
              >
                {CALCS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
            {form.amount_calculation === "fixed" && (
              <label className="block">
                <span className="font-medium text-slate-700">{t("policies.allowances_calc_fixed")}</span>
                <input
                  type="number"
                  step="0.01"
                  value={form.fixed_amount}
                  onChange={(e) =>
                    setForm({ ...form, fixed_amount: e.target.value })
                  }
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2 font-mono"
                />
              </label>
            )}
            {form.amount_calculation === "percent" && (
              <label className="block">
                <span className="font-medium text-slate-700">{t("policies.allowances_percent_label")}</span>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={form.percent}
                  onChange={(e) => setForm({ ...form, percent: e.target.value })}
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2 font-mono"
                />
              </label>
            )}
            {form.amount_calculation === "formula" && (
              <label className="block col-span-2">
                <span className="font-medium text-slate-700">{t("policies.allowances_formula_label")}</span>
                <input
                  type="text"
                  value={form.formula_ref}
                  onChange={(e) =>
                    setForm({ ...form, formula_ref: e.target.value })
                  }
                  placeholder="annual_ticket_eligibility"
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2 font-mono"
                />
              </label>
            )}
          </div>

          <div className="flex flex-wrap gap-3 border-t border-slate-100 pt-2">
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.included_in_gosi}
                onChange={(e) =>
                  setForm({ ...form, included_in_gosi: e.target.checked })
                }
              />
              <span>{t("policies.allowances_in_gosi_base")}</span>
            </label>
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.included_in_eos_base}
                onChange={(e) =>
                  setForm({ ...form, included_in_eos_base: e.target.checked })
                }
              />
              <span>{t("policies.allowances_in_eos_base")}</span>
            </label>
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.included_in_tax}
                onChange={(e) =>
                  setForm({ ...form, included_in_tax: e.target.checked })
                }
              />
              <span>{t("policies.allowances_in_tax_base")}</span>
            </label>
          </div>

          {errorMsg && (
            <p className="rounded bg-rose-50 p-2 text-rose-700">{errorMsg}</p>
          )}
        </form>
    </EntityDrawer>
  );
}

function AllowanceRow({
  a,
  onToggleActive,
  onDelete,
}: {
  a: AllowanceDetail;
  onToggleActive: (id: string, active: boolean) => void;
  onDelete: (id: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <tr className={a.is_active ? "" : "opacity-50"}>
      <td className="px-3 py-2 font-mono text-xs">
        <Link
          to={`/policies/allowances/${a.id}`}
          className="hover:underline"
        >
          {a.code}
        </Link>
      </td>
      <td className="px-3 py-2 text-sm font-medium">
        <Link to={`/policies/allowances/${a.id}`} className="hover:underline">
          <BilingualText
            en={a.name_en}
            ar={a.name_ar}
            secondaryClassName="text-xs text-slate-500"
          />
        </Link>
      </td>
      <td className="px-3 py-2 text-xs">
        <Badge tone={a.allowance_type === "deduction" ? "rose" : a.allowance_type === "cost" ? "slate" : "green"}>
          {a.allowance_type}
        </Badge>
      </td>
      <td className="px-3 py-2 text-xs">{a.category}</td>
      <td className="px-3 py-2 text-xs font-mono">
        {a.amount_calculation === "fixed" && `${a.fixed_amount} ${a.currency}`}
        {a.amount_calculation === "percent" && `${a.percent}%`}
        {a.amount_calculation === "per_day" && `${a.per_day_amount}/day`}
        {a.amount_calculation === "formula" && (
          <span className="italic">{a.formula_ref}</span>
        )}
      </td>
      <td className="px-3 py-2 text-xs">
        {a.included_in_gosi && <Badge tone="sky">GOSI</Badge>}{" "}
        {a.included_in_eos_base && <Badge tone="amber">EOS</Badge>}{" "}
        {a.included_in_tax && <Badge tone="rose">Tax</Badge>}
      </td>
      <td className="px-3 py-2 text-end">
        <button
          type="button"
          onClick={() => onToggleActive(a.id, !a.is_active)}
          className="me-1 rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
          aria-label={t("policies.allowances_deactivate")}
          title={
            a.is_active
              ? t("policies.allowances_deactivate")
              : t("policies.allowances_activate")
          }
        >
          <Power className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => onDelete(a.id)}
          className="rounded p-1 text-rose-500 hover:bg-rose-50 hover:text-rose-700"
          aria-label={t("common.delete")}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </td>
    </tr>
  );
}

export function AllowancesPage() {
  const { t } = useTranslation();
  const { legalEntityId: le, gate } = useLegalEntityGate();

  const [showNew, setShowNew] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<string>("");
  const [typeFilter, setTypeFilter] = useState<string>("");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["allowances", le, categoryFilter, typeFilter, includeInactive],
    queryFn: () =>
      api.listAllowances(le, {
        category: categoryFilter || undefined,
        allowanceType: typeFilter || undefined,
        includeInactive,
      }),
    enabled: !!le,
  });

  const toggleActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.updateAllowance(id, { is_active: active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["allowances"] }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteAllowance(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["allowances"] });
      setErrorMsg(null);
    },
    onError: (e: unknown) => {
      setErrorMsg(e instanceof ApiError ? e.detail : String(e));
    },
  });

  if (gate) return gate;
  if (query.isLoading) return <Loading />;
  if (query.error) return <ErrorPanel message={(query.error as Error).message} />;

  const allowances = query.data ?? [];

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={t("policies.allowances_title")}
        subtitle={t("policies.grades_count", { count: allowances.length })}
      />
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="rounded border border-slate-300 px-2 py-1 text-sm"
          >
            <option value="">{t("policies.allowances_all_categories")}</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="rounded border border-slate-300 px-2 py-1 text-sm"
          >
            <option value="">{t("common.all_types")}</option>
            {TYPES.map((tp) => (
              <option key={tp} value={tp}>
                {tp}
              </option>
            ))}
          </select>
          <label className="inline-flex items-center gap-1 text-xs">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            <span>{t("common.show_inactive")}</span>
          </label>
          <button
            type="button"
            onClick={() => setShowNew(true)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-3 py-1.5 text-sm font-medium text-brand-fg hover:opacity-90"
          >
            <Plus className="h-4 w-4" /> {t("common.new")}
          </button>
        </div>
      </div>

      <StatutoryDefaultsCallout domain="allowance" />

      {errorMsg && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
          {errorMsg}
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="min-w-full divide-y divide-slate-100">
          <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-3 py-2 text-start">{t("policies.code_label")}</th>
              <th className="px-3 py-2 text-start">{t("policies.col_name")}</th>
              <th className="px-3 py-2 text-start">{t("common.type")}</th>
              <th className="px-3 py-2 text-start">{t("policies.allowances_category_label")}</th>
              <th className="px-3 py-2 text-start">{t("policies.col_amount")}</th>
              <th className="px-3 py-2 text-start">{t("policies.col_flags")}</th>
              <th className="px-3 py-2 text-end">{t("policies.col_actions")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {allowances.map((a) => (
              <AllowanceRow
                key={a.id}
                a={a}
                onToggleActive={(id, active) =>
                  toggleActive.mutate({ id, active })
                }
                onDelete={(id) => remove.mutate(id)}
              />
            ))}
            {allowances.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-12 text-center text-sm italic text-text-tertiary">
                  {t("policies.no_match_filters")}{" "}
                  <span className="not-italic">
                    {t(
                      "policies.allowances_empty_hint",
                      "— adjust the filters above or add a new allowance.",
                    )}
                  </span>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showNew && (
        <NewAllowanceDialog
          legalEntityId={le}
          onClose={() => setShowNew(false)}
          onCreated={() => qc.invalidateQueries({ queryKey: ["allowances"] })}
        />
      )}
    </div>
  );
}
